import { useEffect, useRef, useState } from "react"
import {
  HALO_PAD,
  MIX_TAU,
  buildHaloGeometry,
  drawHaloFrame,
  iconAnchors,
  iconRadius,
  stepMix,
} from "../setup/haloGeometry"

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function rasterText(text, style) {
  const probe = document.createElement("canvas").getContext("2d")
  probe.font = style.font
  if ("letterSpacing" in probe) probe.letterSpacing = style.letterSpacing
  const metrics = probe.measureText(text)
  const sampleWidth = Math.max(1, Math.ceil(metrics.width))
  const ascent = Math.max(1, Math.ceil(metrics.actualBoundingBoxAscent || 0))
  const descent = Math.max(0, Math.ceil(metrics.actualBoundingBoxDescent || 0))
  const sampleHeight = Math.max(1, ascent + descent)
  const sample = document.createElement("canvas")
  sample.width = sampleWidth
  sample.height = sampleHeight
  const ctx = sample.getContext("2d", { willReadFrequently: true })
  ctx.font = style.font
  if ("letterSpacing" in ctx) ctx.letterSpacing = style.letterSpacing
  ctx.fillStyle = "#000000"
  ctx.textBaseline = "alphabetic"
  ctx.fillText(text, 0, ascent)
  return { sample, sampleWidth, sampleHeight }
}

export default function FigureHalo({ lit, text, targetRef }) {
  const canvasRef = useRef(null)
  const sceneRef = useRef(null)
  const litRef = useRef(lit)
  const [reduced, setReduced] = useState(prefersReducedMotion)
  const [ready, setReady] = useState(false)
  litRef.current = lit

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const onChange = () => setReduced(query.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  useEffect(() => {
    if (reduced) return undefined
    const target = targetRef.current
    const canvas = canvasRef.current
    if (!target || !canvas) return undefined
    let cancelled = false

    const measure = () => {
      if (cancelled) return
      const style = getComputedStyle(target)
      const painted = rasterText(text, style)
      const { sample, sampleWidth, sampleHeight } = painted
      const cssWidth = sampleWidth + HALO_PAD * 2
      const cssHeight = sampleHeight + HALO_PAD * 2
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      canvas.style.width = `${cssWidth}px`
      canvas.style.height = `${cssHeight}px`
      canvas.width = Math.round(cssWidth * dpr)
      canvas.height = Math.round(cssHeight * dpr)
      const inkLeft = Math.max(0, (target.offsetWidth - sampleWidth) / 2)
      const inkTop = Math.max(0, (target.offsetHeight - sampleHeight) / 2)
      canvas.style.left = `${inkLeft - HALO_PAD}px`
      canvas.style.top = `${inkTop - HALO_PAD}px`
      const pixels = sample
        .getContext("2d", { willReadFrequently: true })
        .getImageData(0, 0, sampleWidth, sampleHeight)
      sceneRef.current = {
        ctx: canvas.getContext("2d"),
        cssWidth,
        cssHeight,
        dpr,
        loops: buildHaloGeometry(pixels.data, sampleWidth, sampleHeight, HALO_PAD),
        anchors: iconAnchors(HALO_PAD, HALO_PAD, sampleWidth, sampleHeight),
        radius: iconRadius(sampleWidth),
        mix: sceneRef.current?.mix ?? 0,
      }
      setReady(true)
    }

    document.fonts.ready.then(() => {
      if (!cancelled) measure()
    })
    const observer = new ResizeObserver(measure)
    observer.observe(target)
    return () => {
      cancelled = true
      observer.disconnect()
      sceneRef.current = null
      setReady(false)
    }
  }, [reduced, targetRef, text])

  useEffect(() => {
    if (reduced || !ready) return undefined
    let frame = 0
    let last = performance.now()
    const tick = (now) => {
      const scene = sceneRef.current
      if (!scene) return
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000))
      last = now
      const target = litRef.current ? 1 : 0
      scene.mix = stepMix(scene.mix, target, dt, MIX_TAU)
      drawHaloFrame(scene.ctx, {
        cssWidth: scene.cssWidth,
        cssHeight: scene.cssHeight,
        dpr: scene.dpr,
        loops: scene.loops,
        anchors: scene.anchors,
        radius: scene.radius,
        mix: scene.mix,
        time: now / 1000,
      })
      if (litRef.current || scene.mix > 0.004) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [lit, ready, reduced])

  if (reduced) return null

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute"
      aria-hidden="true"
    />
  )
}
